import { AssessmentResult } from '@/types/assessment';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { isValidUUID } from '../validation/validationUtils';
import { getResultFromLocalStorage, getResponsesFromLocalStorage } from '../storage/localStorageService';

/**
 * Fetches results for a user from Supabase database
 */
export const getResultsFromDatabase = async (userId: string): Promise<AssessmentResult[]> => {
  // Fetch results from Supabase
  const { data, error } = await supabase
    .from('assessment_results')
    .select('*')
    .eq('user_id', userId as string)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching results:', error);
    toast.error('Failed to fetch results from database');
    return [];
  }

  if (!data || data.length === 0) {
    console.log('No results found in database');
    return [];
  }

  // Transform the data to AssessmentResult[]
  const results: AssessmentResult[] = data.map(item => ({
    id: item.id as string,
    userId: item.user_id as string,
    report: item.report as string,
    pdfUrl: item.pdf_url as string || '',
    createdAt: new Date(item.created_at as string),
    orderId: item.order_id as string || undefined
  }));

  return results;
};

/**
 * Comprehensive function to get results with fallback to local storage
 */
export const getResultsForUser = async (userId: string): Promise<AssessmentResult[]> => {
  try {
    // Check for saved user in localStorage
    console.log('Checking if user exists in local storage');
    const savedUser = localStorage.getItem('user');
    if (!savedUser) {
      console.warn('No user found in localStorage');
    } else {
      console.log('User found in localStorage');
    }
    
    // Check if userId is valid
    if (!userId || typeof userId !== 'string') {
      console.error('Invalid user ID provided:', userId);
      toast.error('Invalid user ID');
      return [];
    }

    console.log('Fetching results for user ID:', userId);

    // Check if userId is a valid UUID before querying Supabase
    if (!isValidUUID(userId)) {
      console.log('User ID is not a valid UUID. Checking localStorage instead.');
      
      // Try to get results from localStorage first
      const localResult = getResultFromLocalStorage(userId);
      if (localResult) {
        console.log('Found result in localStorage:', localResult);
        return [localResult];
      }
      
      // If no result, check for responses
      const localResponses = getResponsesFromLocalStorage(userId);
      if (localResponses) {
        try {
          console.log('Found responses in localStorage:', localResponses);
          
          // Create a mock result from localStorage data
          const mockResult: AssessmentResult = {
            id: 'local-' + Date.now(),
            userId: userId,
            report: 'This is a basic career guidance report based on your assessment. For a more detailed AI-powered report, please add your OpenAI API key in the settings above.',
            pdfUrl: '',
            createdAt: new Date(),
          };
          
          return [mockResult];
        } catch (e) {
          console.error('Error creating mock result:', e);
        }
      }
      
      // If no valid localStorage data or parsing error, return empty array
      console.log('No valid data found in localStorage');
      toast.error('No results found for this user');
      return [];
    }

    // Proceed with Supabase query for valid UUID
    const results = await getResultsFromDatabase(userId);

    if (results.length === 0) {
      // Check if we have responses but no results
      const localResponses = getResponsesFromLocalStorage(userId);
      if (localResponses) {
        console.log('Found responses in localStorage but no results. Consider generating a report.');
        toast.info('You have completed an assessment but no report has been generated yet.');
      }
    }

    return results;
  } catch (error) {
    console.error('Error fetching results:', error);
    toast.error('Error fetching results');
    return [];
  }
};
